// ORI-654 — the unresolved-work-type diagnostic's throttle state + reporter, kept in a
// dependency-free leaf module (imports only `logger`) so BOTH the view-boundary hook
// (useWorkTypeDiagnostics) AND every auth-teardown path (AuthContext.signOut /
// signOutAndClearOnly and helper/api's global 401 handler) can share the reset WITHOUT forming
// an import cycle — the same "reset paths must stay in sync with auth teardown" contract that
// resetUserProfileBootGuard and vendorWelcomeBanner already model. The hook module imports
// react/redux/selectors, so it CANNOT be imported into helper/api, which is itself in the
// store's import graph (work-order slice → api.ts → helper/api).
import { logger } from 'src/helper/logger';

/**
 * How long the same `issueId:selectedOptionId` key stays silent after it is reported.
 *
 * A time window rather than the old permanent per-session dedupe: on each work-order data change
 * that re-derives an unresolved issue, the key re-emits at most once per window, so a recurring
 * failure stays visible to rate-based Datadog alerting. This is NOT a heartbeat — nothing polls;
 * emission is driven by data changes (initial fetch, return-trip, realtime quote frames), so a
 * broken work order left idle with no further activity emits once, not once per window.
 */
export const WORK_TYPE_DIAGNOSTIC_THROTTLE_MS = 5 * 60 * 1000;

// Last time each diagnostic key was reported. Module state, so the throttle spans component
// mounts within a session — and, like the boot guard and the welcome-banner latches, it is
// cleared on every auth teardown (resetWorkTypeDiagnosticsThrottle) so one vendor's throttle
// history never suppresses the next same-tab session's diagnostics.
const lastReportedAt = new Map<string, number>();

/**
 * Clear the throttle window. Load-bearing on auth teardown (see the module header — called from
 * AuthContext.signOut / signOutAndClearOnly and helper/api's 401 handler) and reused by tests
 * for deterministic timing.
 */
export const resetWorkTypeDiagnosticsThrottle = (): void => {
  lastReportedAt.clear();
};

/**
 * Report a single unresolved work-type case at most once per throttle window.
 *
 * The throttle KEY mirrors the previous reducer-side diagnostic exactly —
 * `issueId:selectedOptionId`, or `no-issue-id:<option>` when the issue carries no id — so
 * distinct malformed payloads stay distinct and bounded. The log CONTEXT additionally carries
 * `workOrderId` as a triage anchor (an alert on two opaque option/issue ids is not actionable);
 * the throttle key is deliberately left unchanged so cardinality and existing message-keyed
 * Datadog queries are unaffected. `now` is injectable purely so tests can exercise the window
 * boundary deterministically.
 */
export const reportUnresolvedWorkType = (
  issueId: string | undefined,
  selectedOptionId: string | null | undefined,
  workOrderId?: string,
  now: number = Date.now(),
): void => {
  const key = issueId ? `${issueId}:${selectedOptionId}` : `no-issue-id:${selectedOptionId}`;
  const last = lastReportedAt.get(key);
  if (last !== undefined && now - last < WORK_TYPE_DIAGNOSTIC_THROTTLE_MS) {
    return;
  }

  lastReportedAt.set(key, now);
  logger.warn('[VendorPortal] Issue work type could not be resolved', {
    issueId,
    selectedOptionId,
    workOrderId,
  });
};
